import Badge from "@mui/material/Badge";
import {
  Components,
  Theme,
  ThemeOptions,
  ThemeProvider,
  createTheme,
} from "@mui/material/styles";
import {
  DateCalendar,
  DateCalendarProps,
  DesktopDatePicker,
  DesktopDatePickerProps,
  LocalizationProvider,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import dayjs, { Dayjs } from "dayjs";

const rootOverride = (styles = {}, isRoot = true) =>
  isRoot
    ? {
        styleOverrides: {
          root: styles,
        },
      }
    : { styleOverrides: styles };

type CustomComponents = Components & {
  MuiDateCalendar?: ThemeOptions;
};

const monthYearCalendarStyles = rootOverride({
  maxWidth: "fit-content",
});
const monthYearPickerStyles = rootOverride({
  button: {
    fontSize: 14,
  },
});

const newTheme = (theme: Theme) =>
  createTheme({
    ...theme,
    components: {
      MuiDateCalendar: rootOverride({
        width: 270,
        height: 285,
      }),
      MuiDayCalendar: rootOverride(
        {
          weekDayLabel: {
            width: 30,
            height: 30,
          },
        },
        false,
      ),
      MuiPickersDay: rootOverride({
        width: 30,
        height: 30,
      }),
      MuiPickersCalendarHeader: rootOverride(
        {
          root: {
            ".MuiPickersArrowSwitcher-spacer": { width: 35 },
          },
          labelContainer: {
            minWidth: "fit-content",
          },
          switchViewButton: {
            padding: 0.2,
          },
          label: {
            fontSize: 14,
          },
        },
        false,
      ),
      MuiMonthCalendar: monthYearCalendarStyles,
      MuiYearCalendar: monthYearCalendarStyles,
      MuiPickersMonth: monthYearPickerStyles,
      MuiPickersYear: monthYearPickerStyles,
    } as CustomComponents,
  });

type OriginaDatePickerProps = Omit<DesktopDatePickerProps<Dayjs>, "minDate">;

export type DatePickerFieldProps = OriginaDatePickerProps & {
  date?: Dayjs;
  minDate?: string;
  daysWithAvailability?: string[];
};

const BagedDayPicker = (
  props: PickersDayProps<Dayjs> & { highlightedDays?: string[] },
) => {
  const {
    highlightedDays = [],
    day,
    outsideCurrentMonth,
    selected,
    ...other
  } = props;

  const isSelected =
    !props.outsideCurrentMonth &&
    highlightedDays.includes(props.day.format("YYYY-MM-DD"));

  return (
    <Badge
      key={props.day.toString()}
      overlap="circular"
      variant={isSelected ? "dot" : "standard"}
      badgeContent={isSelected ? "3" : undefined}
      color="success"
    >
      <PickersDay
        {...other}
        outsideCurrentMonth={outsideCurrentMonth}
        day={day}
      />
    </Badge>
  );
};

export const DatePickerField = ({
  date,
  minDate,
  value,
  daysWithAvailability = [],
  ...rest
}: DatePickerFieldProps) => {
  const minDateValue: Dayjs = dayjs(`${minDate}T00:00:00.000`);
  return (
    <ThemeProvider theme={newTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DesktopDatePicker
          minDate={minDate ? minDateValue : undefined}
          views={["year", "month", "day"]}
          value={value ? dayjs(value) : null}
          slots={{
            day: daysWithAvailability.length
              ? // eslint-disable-next-line react/no-unstable-nested-components
                (props) => (
                  <BagedDayPicker
                    {...props}
                    highlightedDays={daysWithAvailability}
                  />
                )
              : undefined,
          }}
          {...rest} // Pass the remaining props
        />
      </LocalizationProvider>
    </ThemeProvider>
  );
};

type OriginaDateCalendarProps = Omit<DateCalendarProps<Dayjs>, "minDate">;
type DatePickerProps = OriginaDateCalendarProps & {
  date?: Dayjs;
  minDate?: string;
};

export const DatePicker = ({ minDate, ...rest }: DatePickerProps) => {
  const minDateValue: Dayjs = dayjs(`${minDate}T00:00:00.000`);
  return (
    <ThemeProvider theme={newTheme}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DateCalendar
          minDate={minDate ? minDateValue : undefined}
          views={["year", "month", "day"]}
          {...rest} // Pass the remaining props
        />
      </LocalizationProvider>
    </ThemeProvider>
  );
};
